// COP — React shell root.
//
// Gates on a real TDFLite/idplite login: no session → the LoginScreen; signed in
// → the COP, booted for that identity (the logged-in user's JWT claims are the
// ABAC subject the LOCAL PDP filters the whole picture against). React renders the
// SHELL ONLY; the render loop lives in startCop() (imperative).
//
// 0.1b "Clean Shell": the shell is the NAMED-IA GUTTER FRAME (docs/ux/
// SHELL-COMPOSITION.md). The map owns ~100% of resting glass; the floor docks into
// the GutterFrame's grid regions (present-but-recessive); summoned surfaces overlay
// inside `.cop` (z60+). Collapse-to-bare hands gutter width to the map at 60fps.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { startCop, INITIAL_COP_STATUS, type CopHandle, type CopStatus, type CopIdentity } from "./host/cop.ts";
import { tdflite, type Session } from "./host/tdflite.ts";
import { createUrlStateController, type UrlStateController } from "./host/urlState.ts";
import { isOAuthCallbackLanding } from "./host/entrance.ts";
import { isSilentReauthFrame, makeTokenRefresher } from "./host/google-oidc.ts";
import * as cert from "./host/cert.ts";
import { getSettingsStore } from "../../../packages/local-settings/src/index.ts";
import { clearKasToken, setTokenRefresher } from "./host/kas.ts";
import { displayNameFor } from "./host/personas.ts";
import type { PolicyBundle, SubjectClaims } from "../../../packages/abac-pdp/src/index.ts";
import { ClassificationBanner } from "./components/ClassificationBanner.tsx";
import { BannerStack } from "./components/BannerStack.tsx";
import type { QueryScopeViewAs, ViewOverlayHandle } from "./components/ViewAsPanel.tsx";
import { LoginScreen } from "./components/LoginScreen.tsx";
import { SplashScreen } from "./components/SplashScreen.tsx";
import { GraphView } from "./components/GraphView.tsx";
import { CopilotPanel } from "./panels/CopilotPanel.tsx";
import { PanelHost } from "./components/PanelHost.tsx";
import { CHAT_CAPABILITY_ID } from "./capabilities/chatCapability.ts";
import type { PanelContribution } from "../../../packages/capability-host/src/index.ts";
import { Toasts, pushToast } from "./components/Toasts.tsx";

// The named-IA shell floor + structural frame. Each is a self-contained component
// that reads the immutable CopStatus snapshot and calls the imperative CopHandle —
// none of them reach the engine/render loop. (Each self-imports its own CSS.)
import { GutterFrame } from "./components/GutterFrame.tsx";
import { FunctionRail, type LeftPanel } from "./components/FunctionRail.tsx";
import { LeftSlideout } from "./components/LeftSlideout.tsx";
import { Ledger } from "./components/Ledger.tsx";
import { TrustModal } from "./components/TrustModal.tsx";
import { SettingsDrawer } from "./components/SettingsDrawer.tsx";
import { Throughline, type DoCommand } from "./components/Throughline.tsx";
import { CensusStrip } from "./components/CensusStrip.tsx";
import { Cartouche } from "./components/Cartouche.tsx";
import "./shell.css";

// The pre-boot status snapshot. Sourced from the host seam's single source of
// truth (INITIAL_COP_STATUS) so it never drifts as the CopStatus contract grows.
const INITIAL_STATUS: CopStatus = INITIAL_COP_STATUS;

// The summoned fusion-graph overlay (z60, over no furniture, one-heavy-at-a-time).
// It is an OVERLAY SIBLING inside `.cop`, not a floor item — summoned from the
// Ledger's "Relationships" action (handle.openGraph → the onGraph seam).
const GRAPH_OVERLAY_STYLE: CSSProperties = {
  position: "absolute", inset: "48px 24px 40px", zIndex: 60,
  background: "var(--color-surface-base, #06090d)", border: "1px solid var(--ui-hairline, rgba(31,182,182,0.22))",
  borderRadius: 6, boxShadow: "0 24px 80px rgba(0,0,0,0.6)", overflow: "hidden",
  display: "flex", flexDirection: "column",
};
const GRAPH_CLOSE_STYLE: CSSProperties = {
  position: "absolute", right: 12, top: 10, zIndex: 61, background: "transparent",
  color: "var(--ui-text-dim, #8b96a3)", border: "1px solid var(--ui-hairline, rgba(31,182,182,0.2))",
  borderRadius: 3, padding: "4px 9px", font: "600 9px/1 ui-monospace, monospace",
  letterSpacing: "0.1em", cursor: "pointer",
};

// The COP Copilot panel — a BOTTOM-CENTER floating CARD OVERLAY SIBLING inside `.cop`
// (mock line 432). It reads the host-filtered (PDP-visible) working set via handle.copilotApi;
// it never reaches the engine. Summoned from the persistent Copilot toggle in the omnibox row
// (and the Throughline DO rung). The WRAPPER owns the float geometry + glass; the CopilotPanel
// fills it (transparent, radius:inherit). z-index 42 sits above the map/floor but below the
// throughline scrim (z60) + Trust modal (z62). bottom:118px lifts it CLEAR of the whole
// bottom furniture band (census strip + Throughline omnibox rung + 22px banner) so it pops
// ABOVE the universal bar instead of overlapping it (matches the IdentityPanel band clearance).
const COPILOT_DOCK_STYLE: CSSProperties = {
  position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: 118,
  width: 620, maxWidth: "calc(100% - 420px)", zIndex: 42,
  background: "rgba(9,14,20,.94)",
  border: "1px solid rgba(31,182,182,.3)",
  borderRadius: "var(--radius-panel, 12px)",
  boxShadow: "0 24px 70px rgba(0,0,0,.6)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  overflow: "hidden",
  display: "flex", flexDirection: "column",
};

export function App(): JSX.Element {
  // SILENT RE-AUTH IFRAME GUARD. A federated silent renewal loads THIS page in a hidden iframe
  // purely to read the fresh `?code` off the same-origin redirect landing (see
  // google-oidc.silentGoogleReauth / iframeSilentCode). Do NOT boot the COP inside that frame:
  // the parent tab reads the code and runs the ONE stateless BFF exchange. Booting here would
  // double-spend the single-use code (racing the parent) and spin up a hidden duplicate map/KAS.
  // Render nothing; the parent owns the exchange. (Only a FRAMED OAuth landing short-circuits —
  // the normal top-level app is untouched.) A thin guard wrapper so AppRoot's hooks stay
  // unconditional (no rules-of-hooks conditional-return smell).
  if (isSilentReauthFrame(window)) return <></>;
  return <AppRoot />;
}

function AppRoot(): JSX.Element {
  const [session, setSession] = useState<Session | null>(() => tdflite.restore());
  const [bundle, setBundle] = useState<PolicyBundle | null>(null);
  // The Phase 3 splash GATE shown BEFORE the existing login (splash → login → COP).
  // A restored session short-circuits below (session != null skips both splash AND login),
  // so this only gates the no-session entrance. Reset to false on logout to return to splash.
  // OAuth redirect landing (?code/?error): counts as "already chose the entrance" so the
  // LoginScreen mounts and its effect completes the Google exchange (see host/entrance.ts).
  // Initializer (not derived per render) so "← Entrance" after a failed exchange stays a real
  // true→false transition.
  const [entered, setEntered] = useState(() => isOAuthCallbackLanding(window.location.href));

  // On (re)login, fetch the LIVE policy bundle from TDFLite once.
  useEffect(() => {
    if (session && !bundle) {
      void tdflite.policyBundle().then(setBundle);
    }
  }, [session, bundle]);

  // SILENT TOKEN REFRESH (the "key withheld after 5 minutes" fix). Give the KAS module a
  // persistence-aware refresher (makeTokenRefresher) that reads the LATEST persisted session
  // (peek — no expiry gate, since the access token may be expired) and renews by session KIND:
  // password/persona → idplite refresh_token rotation; FEDERATED (Google) → silent Google
  // re-verify + BFF re-exchange (federated sessions hold no idplite refresh token by design).
  // Routing through sessionStorage — NOT React state — keeps a refresh from re-booting CopShell
  // and makes it reload-safe. Registered once; kas dedups + generation-guards the actual calls.
  useEffect(() => {
    setTokenRefresher(
      makeTokenRefresher({
        peek: () => tdflite.peek(),
        refresh: (s) => tdflite.refresh(s),
        refreshFederated: (s) => tdflite.refreshFederated(s),
      }),
    );
    return () => setTokenRefresher(null);
  }, []);

  if (!session) {
    // Splash gate first (skipped automatically when a session is restored, since
    // session != null never reaches here). "Enter the TDF COP" advances to the login.
    if (!entered) return <SplashScreen onEnter={() => setEntered(true)} />;
    return <LoginScreen onLoggedIn={setSession} onBack={() => setEntered(false)} />;
  }
  if (!bundle) {
    return (
      <div className="login">
        <div className="login__card login__card--loading">Loading policy from TDFLite…</div>
      </div>
    );
  }
  return (
    <CopShell
      session={session}
      bundle={bundle}
      onSwitch={setSession}
      onLogout={() => {
        tdflite.logout();
        // Belt-and-suspenders: tdflite.logout() already clears these, but call them
        // here too so a sign-out from the shell can never leave identity material live.
        cert.clearCert();
        clearKasToken();
        setSession(null);
        setBundle(null);
        setEntered(false); // back to the splash entrance
      }}
    />
  );
}

/** The booted COP for a signed-in identity. */
function CopShell({
  session,
  bundle,
  onSwitch,
  onLogout,
}: {
  session: Session;
  bundle: PolicyBundle;
  onSwitch: (s: Session) => void;
  onLogout: () => void;
}): JSX.Element {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<CopHandle | null>(null);
  // URL STATE CAPTURE — shareable "look at this one" deep links. Created after boot (so
  // a hash OVERRIDES persisted display prefs); reflects camera/selection/mode/view into
  // the URL fragment + restores them on load. The ⌘K "Copy link" command reads it.
  const urlStateRef = useRef<UrlStateController | null>(null);
  const [status, setStatus] = useState<CopStatus>(INITIAL_STATUS);
  const [graph, setGraph] = useState<{ nodes: unknown[]; edges: unknown[] } | null>(null);
  // The summoned fusion graph: an entity id (from the Ledger's openGraph) OR the
  // empty-string sentinel for the global graph (the Throughline OPEN rung / its DO
  // command). null = dismissed. Driven off the host's onGraph seam.
  const [graphFor, setGraphFor] = useState<string | null>(null);
  // Collapse-to-bare: zeroes BOTH gutters + census so the map owns 100% of glass.
  // One keystroke (`\`) toggles it — the load-bearing collapse WOW (§4).
  const [bare, setBare] = useState(false);
  // The summoned COP Copilot panel (overlay sibling, like the fusion graph). Off by
  // default; toggled from the Throughline DO rung. It renders the C3 copilot
  // capability's CopilotApi (threaded out via handle.copilotApi). null/false = dismissed.
  const [copilotOpen, setCopilotOpen] = useState(false);
  // The shared "Trust & provenance" modal (rule 5: ONE tdfOpen state). Opened by
  // BOTH the inspector masthead "i" button AND the grey lock in the PROTECTED
  // CONTENT addendum; it EXCLUSIVELY owns the KAS verdict display (rule 4). A
  // browser-lock-style overlay sibling inside `.cop` (z62, over the gutter frame).
  const [tdfOpen, setTdfOpen] = useState(false);
  // The HYBRID LEFT SLIDEOUT pane (Tier-3): null = closed; one of the 6 capability
  // panes (search/layers/request/fires/ew/audit) summoned by the FunctionRail. The
  // slideout is a MAP OVERLAY (map does NOT reflow), dismissed by its ✕ or the Esc
  // stack. The "layers" pane hosts the migrated LivingSpine feed ledger.
  const [leftPanel, setLeftPanel] = useState<LeftPanel>(null);
  // Bumps once startCop resolves so the panel can read handleRef.current.copilotApi.
  const [booted, setBooted] = useState(false);
  // Seam B — the host-enumerated panel contributions (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
  // <PanelHost> renders each spec that carries a `mount` (a third-party add-in panel) into a
  // host-owned dock; the copilot's PanelSpec has NO mount, so it is skipped here and stays on
  // the legacy App.tsx side-channel below (migration deferred — see PanelHost header). Re-read
  // off the host's onPanelsChanged signal because capability activation is async.
  const [panels, setPanels] = useState<PanelContribution[]>([]);

  // Load the fused multi-INT entity graph (served from public/data). The graph view
  // runs every node + edge through the SAME local PDP as the map, for this persona.
  useEffect(() => {
    void fetch("/data/fused-entity-graph.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((g) => setGraph(g as { nodes: unknown[]; edges: unknown[] } | null))
      .catch(() => setGraph(null));
  }, []);

  // Boot once; persona switches call handle.setIdentity (no re-boot).
  useEffect(() => {
    const container = mapRef.current;
    if (!container) return;

    let unsub: (() => void) | null = null;
    let unsubGraph: (() => void) | null = null;
    let unsubPanels: (() => void) | null = null;
    let cancelled = false;

    const identity: CopIdentity = {
      bundle,
      claims: session.claims,
      token: session.token,
      label: session.username,
    };
    // Per-persona settings scope (Bundle B): set BEFORE startCop so the DeckBackend's
    // getSettings() at construction reads THIS persona's persisted render params.
    getSettingsStore().setPersona(session.username);
    void startCop(container, identity).then((h) => {
      if (cancelled) {
        void h.dispose();
        return;
      }
      handleRef.current = h;
      // Re-apply THIS persona's persisted display prefs (Bundle B): theme + marker mode are
      // saved on change but only take effect here at boot (the store was scoped via setPersona
      // above). Display-only — never changes clearance/visibility. Layout stays device-auto.
      const persistedPrefs = getSettingsStore().getSettings();
      h.setRenderProfile(persistedPrefs.renderProfile);
      h.setMarkerMode(persistedPrefs.markerMode);
      setBooted(true); // expose handle.copilotApi to the panel render
      unsub = h.onStatus(setStatus);
      // The Ledger summons the GraphView via handle.openGraph → this seam (React
      // never reaches the engine). An entity id summons the graph for that entity.
      unsubGraph = h.onGraph((id) => setGraphFor(id));
      // Seam B — re-read the host's panel contributions whenever the set changes (each
      // capability activation). onPanelsChanged invokes the cb once on subscribe, so this
      // also picks up the boot-time set. <PanelHost> mounts each add-in panel generically.
      unsubPanels = h.onPanelsChanged(() => setPanels(h.panels()));
      // URL STATE CAPTURE — wire AFTER the persisted prefs above, so if the load URL
      // carries a #@… hash it OVERRIDES them (camera + marker/view mode) and restores the
      // selection through the fail-closed PDP/KAS select path. With no hash it is inert
      // and leaves persisted behavior intact. Reflects live state back into the fragment.
      urlStateRef.current = createUrlStateController(h);
    });

    return () => {
      cancelled = true;
      unsub?.();
      unsubGraph?.();
      unsubPanels?.();
      urlStateRef.current?.dispose();
      urlStateRef.current = null;
      void handleRef.current?.dispose();
      handleRef.current = null;
      setBooted(false);
      setPanels([]);
    };
    // Boot for the INITIAL identity only; switching is handled imperatively below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Collapse-to-bare keystroke: `\` toggles `bare` (ignored while typing in a field
  // so it never steals a real backslash). After the toggle, the GutterFrame's grid
  // reflow fires its ResizeObserver → onLayoutSettled → handle.refitMap(); we ALSO
  // fire refitMap directly so the polled-nudge schedule [0,32,80,160]ms runs on the
  // explicit collapse (belt-and-suspenders, §4.3). Reduced-motion is honored: the
  // collapse is a static one-shot grid recompose (no width transition).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "\\" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      setBare((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Run the nudge schedule on every explicit collapse/expand (the authoritative
  // settle also comes through GutterFrame.onLayoutSettled; both call the idempotent
  // held-camera refitMap, so no track slides). Collapse-to-bare also dismisses the
  // left slideout — it hangs off the rail, which is collapsing with the frame.
  useEffect(() => {
    handleRef.current?.refitMap();
    if (bare) setLeftPanel(null);
  }, [bare]);

  // The rail's panel buttons TOGGLE: clicking an already-open pane closes it.
  const toggleLeftPanel = (pane: Exclude<LeftPanel, null>): void =>
    setLeftPanel((cur) => (cur === pane ? null : pane));

  // The Trust modal renders over the SELECTED envelope; deselecting must dismiss it
  // (it has no subject otherwise). A pure derived close — never opens on its own.
  useEffect(() => {
    if (!status.selected && tdfOpen) setTdfOpen(false);
  }, [status.selected, tdfOpen]);

  // Opening Trust RETRIES a NO-DECISION rewrap (state "unavailable" — an auth/transport
  // failure like an expired session token mid-refresh). A no-op for GRANT / policy-DENY /
  // idle, so a real verdict is never re-decided by merely reopening the modal.
  useEffect(() => {
    if (tdfOpen) handleRef.current?.retryDecrypt();
  }, [tdfOpen]);

  const switchPersona = async (username: string, password: string): Promise<void> => {
    if (username === session.username) return;
    try {
      const next = await tdflite.login(username, password);
      handleRef.current?.setIdentity(next.claims as SubjectClaims, next.token);
      getSettingsStore().setPersona(next.username); // Bundle B — re-scope settings to the new persona
      // C4 — RELABEL the copilot header's ACTING-AS tag on the persona switch. The capability is
      // NOT recreated by setIdentity (the host-filtered bus re-scopes the working set in place), so
      // the header's personaShort would otherwise stay frozen at boot. Mirrors cop.ts's boot
      // derivation: displayNameFor(username || "OPERATOR").toUpperCase() — the REAL persona name
      // (e.g. "JAMES RODRIGUEZ", not "JAMES.RODRIGUEZ"). Display only — NOT a security boundary;
      // the cert/JWT/KAS path keys off the raw username in next.claims/next.token, untouched here.
      handleRef.current?.copilotApi.setPersonaShort(displayNameFor(next.username || "OPERATOR").toUpperCase());
      onSwitch(next);
      pushToast({ title: "Identity", body: `Now signed in as ${username}`, tone: "info" });
    } catch (e) {
      pushToast({ title: "Switch failed", body: e instanceof Error ? e.message : "login failed", tone: "alert" });
    }
  };

  // ⌘K "Copy link to this view": copy the CURRENT shareable URL (getShareUrl force-flushes
  // the live camera/selection into the fragment first, so the copied link is never a
  // debounce-tick stale). navigator.clipboard is fail-SOFT — if it's blocked/absent we
  // toast the raw URL so the operator can copy it by hand. Sharer-responsibility: a shared
  // link that carries a TS entity id leaks that entity's existence + rough location to the
  // recipient (content stays sealed to them) — an accepted MVP tradeoff (see urlState.ts).
  const copyViewLink = async (): Promise<void> => {
    const url = urlStateRef.current?.getShareUrl();
    if (!url) {
      pushToast({ title: "Copy link", body: "View link unavailable yet — try again in a moment.", tone: "alert" });
      return;
    }
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        pushToast({ title: "Link copied", body: "Opens this exact view. Sealed content stays sealed to whoever you send it to.", tone: "success" });
        return;
      } catch {
        /* clipboard blocked (no user gesture / permissions) — fall through to manual copy */
      }
    }
    pushToast({ title: "Copy this link", body: url, tone: "info", durationMs: 0 });
  };

  // The existing palette commands fold into the Throughline DO rung (structurally a
  // DoCommand: { id, title, hint?, run }). They run client-side via their own run().
  // The folded-in DO commands. `group` annotates each into the mock's palette taxonomy
  // (ACTION/PLUGIN/AI/SCOPE/ACCESS/VIEW/AUDIT/IDENT, mock paletteItems line 1070) — rendered
  // as a per-row teal mono group column in the Throughline DO rung. The taxonomy is display-
  // only; `run` is unchanged (client-side via the existing seam).
  const commands: DoCommand[] = [
    { id: "fusion-graph", title: graphFor !== null ? "Hide fusion entity graph" : "Open fusion entity graph", hint: "intel", group: "PLUGIN", run: () => setGraphFor((g) => (g === null ? "" : null)) },
    { id: "copilot", title: copilotOpen ? "Hide COP Copilot" : "Open COP Copilot", hint: "analyst", group: "AI", run: () => setCopilotOpen((v) => !v) },
    { id: "clear-selection", title: "Clear selection", hint: "edit", group: "ACTION", run: () => handleRef.current?.clearSelection() },
    { id: "copy-view-link", title: "Copy link to this view", hint: "share", group: "ACTION", run: () => void copyViewLink() },
    { id: "share-location", title: status.shareLocation ? "Share my location: OFF (stop)" : "Share my location: ON (true position)", hint: "presence", group: "ACTION", run: () => void handleRef.current?.setShareLocation(!status.shareLocation) },
    { id: "call-for-evac", title: "Call for evac", hint: "life-safety", group: "ACTION", run: () => void handleRef.current?.callForEvac() },
    { id: "center-theater", title: "Center on theater", hint: "view", group: "VIEW", run: () => handleRef.current?.recenter() },
    { id: "collapse-bare", title: bare ? "Expand the floor" : "Collapse to bare map", hint: "\\", group: "VIEW", run: () => setBare((v) => !v) },
    { id: "layout-cockpit", title: "Layout: Cockpit", hint: "view", group: "VIEW", run: () => handleRef.current?.setLayout("cockpit") },
    { id: "layout-field", title: "Layout: Field", hint: "view", group: "VIEW", run: () => handleRef.current?.setLayout("field") },
    { id: "layout-workbench", title: "Layout: Workbench", hint: "view", group: "VIEW", run: () => handleRef.current?.setLayout("workbench") },
    { id: "view-2d", title: "View: 2D plan (flat)", hint: "view", group: "VIEW", run: () => handleRef.current?.setViewMode("2d") },
    { id: "view-25d", title: "View: 2.5D (tilt for altitude)", hint: "view", group: "VIEW", run: () => handleRef.current?.setViewMode("2.5d") },
    { id: "symbology-milsym", title: "Symbology: Standard (2525 frames)", hint: "view", group: "VIEW", run: () => handleRef.current?.setMarkerMode("milsym") },
    { id: "symbology-dot", title: "Symbology: Minimal (dots)", hint: "view", group: "VIEW", run: () => handleRef.current?.setMarkerMode("dot") },
    { id: "symbology-coalition", title: "Symbology: Coalition (full MIL-STD-2525)", hint: "view", group: "VIEW", run: () => handleRef.current?.setMarkerMode("coalition") },
    { id: "render-dark", title: "Render profile: Dark", hint: "view", group: "VIEW", run: () => handleRef.current?.setRenderProfile("dark") },
    { id: "render-mono-nvg", title: "Render profile: Mono / NVG", hint: "view", group: "VIEW", run: () => handleRef.current?.setRenderProfile("mono-nvg") },
    { id: "render-high-contrast", title: "Render profile: High-contrast", hint: "view", group: "VIEW", run: () => handleRef.current?.setRenderProfile("high-contrast") },
    { id: "render-intel", title: "Render profile: Intel", hint: "view", group: "VIEW", run: () => handleRef.current?.setRenderProfile("intel-skin") },
    { id: "sign-out", title: "Sign out", hint: "identity", group: "IDENT", run: onLogout },
  ];

  const handle = handleRef.current;

  return (
    <div
      className="cop"
      // The right gutter (Ledger) is EMPTY until a SELECT: it expands to its
      // inspector width only while something is selected (cockpit/workbench). This
      // attribute drives that expansion in shell.css — at rest the map owns the
      // column. A withheld object never selects (Glomar), so this never leaks.
      data-selected={status.selected ? "" : undefined}
    >
      <GutterFrame
        layout={status.layout}
        bare={bare}
        onLayoutSettled={() => handleRef.current?.refitMap()}
        slots={{
          // BANNER (row 1): the clearance-aware DUAL banner ONLY — query level +
          // down-filter dropdown over the as-rendered classification. CLASSIFICATION
          // ONLY: no identity/persona/clock sits in or overlaps the banner (the
          // banner-identity invariant). Operator identity lives in the lower-left
          // IdentityPanel HUD; the persona SWITCH affordance is the rail-bottom
          // Profile control (see leftGutter / the overlay siblings below).
          bannerTop: (
            <BannerStack
              highWater={status.highWater}
              queryScope={status.queryScope}
              onQueryLevel={(lvl) => handleRef.current?.setQueryLevel(lvl)}
              // VIEW-AS overlay seam (host lane owns setViewOverlay on CopHandle +
              // viewAs on QueryScope). Consumed via a structural cast so this UI lane
              // typechecks in isolation and binds to the real fields once the host merges.
              viewAs={(status.queryScope as unknown as QueryScopeViewAs).viewAs ?? null}
              onViewOverlay={(spec) =>
                (handleRef.current as unknown as ViewOverlayHandle | null)?.setViewOverlay(spec)
              }
              timeWindow={status.timeWindow}
              onStepTime={(dir) => handleRef.current?.stepTimeWindow(dir)}
              kasOffline={status.kasOffline}
            />
          ),
          // LEFT GUTTER (col 1): the 54px HYBRID FUNCTION RAIL (Tier-3). It carries
          // the migrated spine instruments — the VerdictGlyph (top, drill behavior
          // intact), the 9 mock icon buttons that summon the LeftSlideout panes
          // (layers = the migrated LivingSpine ledger), the preserved deferred
          // ContributionAnchor slot, and the rail-bottom PROFILE control (the
          // PersonaSwitcher icon — the real idplite re-login is UNCHANGED, only the
          // trigger affordance changed). The request button carries a teal count
          // badge wired to the Glomar-safe census.requestable forward placeholder.
          leftGutter: (
            <FunctionRail
              status={status}
              handle={handle as CopHandle}
              leftPanel={leftPanel}
              onOpenPanel={toggleLeftPanel}
              personaCurrent={session.username}
              personaSummary={session.summary}
              onSwitchPersona={(u, p) => void switchPersona(u, p)}
              onLogout={onLogout}
            />
          ),
          // MAP (col 2): the full-bleed `.cop__map` div, moved VERBATIM so the
          // host's mapRef + startCop container wiring is unchanged.
          mapSlot: <div className="cop__map" ref={mapRef} />,
          // RIGHT GUTTER (col 3): THE LEDGER (empty → picture summary; re-skins on
          // SELECT, provenance-masthead-first). `onOpenTrust` opens the ONE shared
          // Trust & provenance modal from BOTH the masthead "i" and the grey lock.
          rightGutter: (
            <Ledger
              status={status}
              handle={handle as CopHandle}
              onOpenTrust={() => setTdfOpen(true)}
            />
          ),
          // CENSUS (row 3): the honest RELEASED · REDUCED · (cond) REQUESTABLE triad pill
          // + caption (always visible; Glomar-safe — no hidden universe, REQUESTABLE is a
          // forward-looking 0 placeholder that only renders when >0).
          census: <CensusStrip status={status} />,
          // THROUGHLINE (row 4): the one persistent omnibox rung; the palette
          // commands fold in as DO entries. The persistent Copilot toggle + the "Top
          // threats" prompt chip live in the resting rung (mock line 498) and drive the
          // SAME copilotOpen state (the toggle) / the copilot ask path (the chip).
          throughline: (
            <Throughline
              status={status}
              handle={handle}
              commands={commands}
              copilotOpen={copilotOpen}
              onToggleCopilot={() => setCopilotOpen((v) => !v)}
              onPrompt={(q) => {
                // The "Top threats" prompt chip: open the copilot + ask it that query.
                // Glomar-safe — the copilot reasons only over the host-filtered visible set.
                setCopilotOpen(true);
                if (booted) void handleRef.current?.copilotApi.ask(q);
              }}
              onAsk={(text) => {
                // C3 (RATIFIED): a bare free-text ASK in the omnibox auto-opens the bottom-center
                // COP Copilot thread + asks it. The copilot reasons ONLY over the host-filtered
                // (PDP-visible) working set — Glomar-safe by construction.
                setCopilotOpen(true);
                if (booted) void handleRef.current?.copilotApi.ask(text);
              }}
            />
          ),
          // BANNER (bottom edge): classification bottom, overlay z30.
          bannerBottom: <ClassificationBanner highWater={status.highWater} position="bottom" />,
        }}
      />

      {/* ── OVERLAY SIBLINGS inside `.cop` (over the frame, never docked) ── */}

      {/* The 278px MULTI-CAPABILITY LEFT SLIDEOUT (Tier-3). A MAP OVERLAY anchored to
          the right of the 54px rail (left:54px, z38) — the map does NOT reflow when it
          opens (map-primacy). Summoned by the rail buttons via `leftPanel`; dismissed
          by its ✕ or the shared Esc stack (LIFO). The "layers" pane hosts the migrated
          LivingSpine; KAS verdicts live ONLY in the audit pane + the Trust modal. */}
      <LeftSlideout
        pane={bare ? null : leftPanel}
        status={status}
        handle={handle as CopHandle}
        // CHAT re-home: the encrypted-chat ChatApi is threaded off the host seam so the
        // "chat" pane renders <ChatPanel api={…}/> inside the LEFT slideout (a dedicated rail
        // toggle), instead of the stuck right-side PanelHost dock. `booted` guards the handle.
        chatApi={booted && handle ? handle.chatApi : undefined}
        onClose={() => setLeftPanel(null)}
      />

      {/* Operator identity lives in the rail-bottom Profile popover (PersonaSwitcher),
          NOT a persistent lower-left HUD — click the user button to see/switch persona.
          (Banners stay classification-only; the popover carries name + clearance.) */}

      {/* The bottom-right map-chrome readout: named edition/fidelity + coord/MGRS. */}
      <Cartouche edition={status.edition} cursor={status.cursor} />

      {/* The summoned fusion entity graph (z60, one-heavy, over no furniture). */}
      {graphFor !== null && graph && (
        <div style={GRAPH_OVERLAY_STYLE}>
          <button type="button" onClick={() => setGraphFor(null)} style={GRAPH_CLOSE_STYLE}>
            ✕ CLOSE
          </button>
          <GraphView graph={graph as { nodes: unknown[]; edges: unknown[] }} bundle={bundle} claims={session.claims as SubjectClaims} />
        </div>
      )}

      {/* The summoned COP Copilot panel — bottom-center float (mock line 432). `booted` guards
          handle.copilotApi being ready (startCop resolved). It reasons ONLY over the host-filtered
          visible set. EJECT (in the panel header) clears the thread AND closes this dock. */}
      {copilotOpen && booted && handle && (
        <div style={COPILOT_DOCK_STYLE}>
          <CopilotPanel
            api={handle.copilotApi}
            sessionClassification={status.highWater.classification}
            onEject={() => setCopilotOpen(false)}
            onSelect={(id) => {
              // C1 — CITE→MAP: SELECT (Ledger re-skin / KAS rewrap) + fly-to the cited entity.
              // Both seams are Glomar-safe visible-only lookups — a hidden/unknown id resolves to
              // a no-op (select → deselect, locate → no fly), never confirming existence.
              handle.select(id);
              handle.locate(id);
            }}
          />
        </div>
      )}

      {/* Seam B — GENERIC add-in panels mounted through the PUBLIC C3 plugin API
          (CONTRACT:C3-CAPABILITY-PLUGIN.1.0). Each host-enumerated PanelSpec that carries
          a `mount` renderer gets a host-owned, location-aware dock; the capability mounts
          its OWN content inside (no per-capability branching here — App.tsx never reaches a
          capability). The copilot's PanelSpec has no `mount`, so it is NOT rendered here —
          it stays on its bespoke dock above (migration deferred; see PanelHost header). The
          P3 encrypted-chat add-in mounts through THIS seam — EXCEPT chat, which is
          re-homed into the LEFT slideout (the dedicated rail toggle) and filtered out here
          so it does not ALSO mount in the stuck right-side dock (no double mount). */}
      <PanelHost panels={panels.filter((p) => p.spec.id !== `${CHAT_CAPABILITY_ID}:panel`)} />

      {/* The ONE shared Trust & provenance modal (z62). Opened by the inspector's
          masthead "i" + the grey lock (rule 5); EXCLUSIVELY owns the KAS verdict
          (rule 4). A pure projection over status.selected + status.decrypt. */}
      <TrustModal status={status} open={tdfOpen} onClose={() => setTdfOpen(false)} />

      {/* The summoned Display settings drawer (z-overlay sibling). Renders nothing at
          rest; it holds the R-LAW-3 "settings" heavy slot (summoning any other heavy
          surface collapses it) and self-manages Esc/scrim close. The rail's gear button
          claims the slot. A pure display-preference surface — no per-object aggregate,
          nothing to leak (Glomar-safe). */}
      <SettingsDrawer status={status} handle={handle} />

      {/* Mount-once toast host. */}
      <Toasts />
    </div>
  );
}

/** True when a keystroke originated inside an editable control — so a bare collapse
 *  key there types a literal char instead of toggling the floor. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}
